import React, {CSSProperties, FC, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import './ContentBlockContainer.less';
import '../../stylesheets/container-styles.less';
import * as ContentBlockActions from "../../store/actions/contentBlockActions";
import {connect} from "react-redux";
import IconButton from '../IconButton/IconButton';
import Icons from '../IconButton/Icons';
import locals from '../../lib/localStorage';
import TabBar from '../TabBar/TabBar';
import {isPortalUser} from "../../lib/picos/currentLogin";
import getParents from "../../lib/picos/getParents";
import {isMobileView} from "../../lib/picos/browserFns";
import {MOBILE_WIDTH} from "../../lib/picos/breakpoints";
import ErrorBoundaryO from "../../ErrorBoundary/ErrorBoundaryO";
import ContentBlock, {CollapseCallback, ContentBlockChildProps, ContentBlockProps} from "./ContentBlock";
import type {RootDispatch, RootState} from "../../@types/store";
import {LocalStorageType, useLocalStorage} from "../../hooks/useLocalStorage";
import useComponentCache from "../../hooks/useComponentCache";
import {useLocation} from "wouter";

function noop() {
}

/**
 * How many ContentBlockContainers sit above this one in the React tree.
 * Query-derived block EXPANSION (deep links like `?expense=<id>`) is
 * page-level behavior: only the outermost container may honor it. A container
 * nested inside another container's block (e.g. the Receipts and Notes block
 * inside an ExpenseCard) must never be forced fullscreen by a query param
 * that was set for an ancestor's table — `?expense=` substring-matches the
 * nested `cb-expense-documents-<id>` title and trapped users in an
 * un-collapsible fixed overlay that buried the expense editor's OK button
 * (OWA-503).
 */
const ContentBlockNestingContext = React.createContext(0);

type ContentBlockElement = typeof ContentBlock & ReactElement & {
    props: Partial<ContentBlockProps>
}

export type SizeProps = {
    gridColumns: number,
    gridRows: number,
    containerWidth: number,
    containerHeight: number,
    useSmallDisplay: boolean,
    ancestorCount: number,
}

type ContentBlockContainerState = {
    expandedBlock: string | null,
    selectedTitle: string,
    singleViewMode: boolean,
    sizeProps: SizeProps,
}

const stripIfs = (child: any): any => {
    // Single component
    if (child.props.hasOwnProperty('test')) {
        // If test = false, we ignore
        if (child.props.test === false) return null;

        // Otherwise we strip each child
        else return React.Children.toArray(child.props.children).flat().map(c => stripIfs(c));
    }
    // Regular single component
    else return child;
}

const ContentBlockContainer: FC<ContentBlockContainerProps> = props => {
    const key = props.name;
    const [location, navigate] = useLocation();
    const nestingDepth = useContext(ContentBlockNestingContext);

    const children = useMemo(() => {
        return React.Children
            .toArray(props.children)
            .flat()
            .filter(c => !!c)
            .map(c => stripIfs(c))
            .flat()
            .filter(c => !!c) as ContentBlockElement[]
    }, [props.children]);

    const showViewToggles = props.showViewToggles ?? true;

    // Get the stored mode for this view
    // Valid values are 'tabbed', 'all' or 'unset'
    const [storedViewMode, setStoredViewMode] = useLocalStorage(`outlaw.singleViewMode.${props.displayName || props.name}`, LocalStorageType.string, 'tabbed'); // Default to tabbed view
    const [selectedTab, setSelectedTab] = useLocalStorage(`outlaw.selectedTab.${props.name}`, LocalStorageType.string, ''); // This should be the first tab if none found

    const getInitialViewMode = useMemo(() => {
        // Should we show the singleViewMode?  We store preferences in localstorage, and we can also provide a
        // default in props.singleViewMode.  If showViewToggles is off, we always use what is in props.singleViewMode.
        let singleViewMode: boolean | undefined;

        // If showViewToggles is off, ignore and use what is in props.singleViewMode
        if (!showViewToggles) singleViewMode = props.singleViewMode;
        else {
            // See if we have anything stored. If so, this overrides props.singleViewMode
            if (storedViewMode === 'unset' || isPortalUser()) singleViewMode = props.singleViewMode ?? false;
            else singleViewMode = storedViewMode === 'tabbed';
        }

        return singleViewMode;
    }, [props.singleViewMode, showViewToggles, storedViewMode]);

    const {cacheValue, cacheSave} = useComponentCache<ContentBlockContainerState>(key);

    const [expandedBlock, setExpandedBlock] = useState<string | null>(cacheValue('expandedBlock', props.expandedBlock ?? ''));
    const [selectedTitle, setSelectedTitle] = useState<string>(cacheValue('selectedTitle', ''));
    const [singleViewMode, setSingleViewMode] = useState<boolean>(cacheValue('singleViewMode', getInitialViewMode ?? false));

    const [splitView, setSplitView] = useLocalStorage(`outlaw.splitView.${props.displayName || props.name}`, LocalStorageType.boolean, false);

    // Build a map from title → urlKey from children props (for URL-based tab selection)
    const urlKeyMap = useMemo(() => {
        const map = new Map<string, string>();
        children.forEach(child => {
            if (child.props?.urlKey) {
                map.set(child.props.title, child.props.urlKey);
            }
        });
        return map;
    }, [children]);

    // Derive selected tab from URL when basePath is set (avoids setState in effect)
    const urlDerivedTitle = useMemo(() => {
        if (!props.basePath) return null;

        const pathAfterBase = location.startsWith(props.basePath)
                              ? location.slice(props.basePath.length)
                              : '';
        const segment = pathAfterBase.split('/').filter(Boolean)[0];

        if (segment) {
            const matchingChild = children.find(child => child.props?.urlKey === segment);
            return matchingChild?.props?.title ?? null;
        }
        return null;
    }, [location, props.basePath, children]);

    // Derive selected tab from URL query params when no basePath/urlKey matching worked.
    // Matches query param keys (e.g., 'expense', 'flat-fee') against ContentBlock titles.
    // ContentBlock titles follow the pattern 'cb-{entity}-{queryKeyPlural}-{id}',
    // e.g., 'cb-matter-expenses-abc123' contains 'expense'.
    //
    // PAGE-LEVEL ONLY (nestingDepth === 0). This is a deliberately loose
    // SUBSTRING match against a GLOBAL signal (window.location.search), so a
    // nested container would otherwise match a param meant for an ancestor:
    // `?expense=` matches the editor-internal `cb-expense-documents-<id>` (and
    // any tab whose title merely contains "document"/"expense"), hijacking the
    // nested view away from its own default/initialTab and — in boxes mode —
    // force-expanding it fullscreen over the editor (OWA-503). Deep-linking a
    // block/tab is a page-level navigation concern; only the outermost
    // container responds to it. urlDerivedTitle (explicit per-container
    // basePath) is intentional and is NOT gated here.
    const queryDerivedTitle = useMemo(() => {
        if (urlDerivedTitle) return null;
        if (nestingDepth !== 0) return null;

        const search = window.location.search;
        if (!search) return null;

        const params = new URLSearchParams(search);
        const queryKeys = Array.from(params.keys()).filter(k => k !== 'item');
        if (queryKeys.length === 0) return null;

        for (const qk of queryKeys) {
            const normalized = qk.replace(/-/g, '').toLowerCase();
            const match = children.find(child => {
                const title = (child.props?.title || '').replace(/-/g, '').toLowerCase();
                return title.includes(normalized);
            });
            if (match) return match.props?.title ?? null;
        }
        return null;
    }, [urlDerivedTitle, children, nestingDepth]);

    // In boxes mode, treat URL-derived block like an expanded block (full-width, full-height).
    // This is the boxes-mode sibling of effectiveTab's precedence (see OWA-500
    // in renderTabbedInterface): there, a user's tab click (selectedTitle) was
    // made to outrank the deep-link sources; here, the user's expand/collapse
    // must likewise be able to outrank the query-derived expansion. Two guards:
    //   1. Only the OUTERMOST container (nestingDepth 0) honors query-derived
    //      expansion — block expansion from a URL is a page-level navigation
    //      concern. A nested container (e.g. Receipts and Notes inside the
    //      expense editor) must never be force-expanded fullscreen because its
    //      title substring-matches an ancestor's param (?expense= ⊃
    //      cb-expense-documents-<id>), which buried the editor's OK check — OWA-503.
    //   2. Once the user collapses the URL/query-derived block, their action
    //      wins: the same title may not re-pin the expansion. The query param
    //      stays in the URL for the whole edit, so without this dismissal the
    //      collapse button silently does nothing — the boxes-mode form of the
    //      OWA-500 "deep link permanently outranks the user" defect.
    const [dismissedUrlTitle, setDismissedUrlTitle] = useState<string | null>(null);
    const urlOrQueryTitle = urlDerivedTitle || queryDerivedTitle;
    const effectiveExpandedBlock = useMemo(() => {
        if (!singleViewMode && urlOrQueryTitle && nestingDepth === 0 &&
            urlOrQueryTitle !== dismissedUrlTitle) return urlOrQueryTitle;
        return expandedBlock;
    }, [singleViewMode, urlOrQueryTitle, expandedBlock, nestingDepth, dismissedUrlTitle]);

    // Navigating to a NEW deep-link target invalidates a previous dismissal
    useEffect(() => {
        if (urlOrQueryTitle && dismissedUrlTitle && urlOrQueryTitle !== dismissedUrlTitle) {
            setDismissedUrlTitle(null);
        }
    }, [urlOrQueryTitle, dismissedUrlTitle]);

    // A document deep link (props.initialTab) can arrive or change while this
    // container is already mounted — e.g. opening a document for a contact whose
    // card is already showing. The container name excludes the documentId, so the
    // prop updates without a remount. When initialTab changes to a new tab, jump
    // to it (a later user click still overrides it via selectedTitle/effectiveTab).
    // We compare against the previous value so this never fires on the initial
    // mount: the first deep link is already honored by effectiveTab's fallback
    // chain, and firing here would write the deep-link tab into the cached
    // selectedTitle, leaving it stuck after the deep link is gone.
    const prevInitialTab = useRef(props.initialTab);
    useEffect(() => {
        if (props.initialTab && props.initialTab !== prevInitialTab.current) {
            setSelectedTitle(props.initialTab);
        }
        prevInitialTab.current = props.initialTab;
    }, [props.initialTab, setSelectedTitle]);

    const initialSizeProps: SizeProps = {
        gridColumns: Math.min(Math.floor(window.innerWidth / (props.breakWidth ?? 550)), React.Children.count(children)),
        gridRows: undefined,
        containerWidth: window.innerWidth,
        containerHeight: window.innerHeight,
        useSmallDisplay: singleViewMode
                         ? false
                         : (window.innerWidth < MOBILE_WIDTH) || !expandedBlock,
        ancestorCount: 0
    };

    const [sizeProps, setSizeProps] = useState<SizeProps>(cacheValue('sizeProps', initialSizeProps));

    // Handle resize events to figure out how many components per row
    const updateDimensions = useCallback(() => {
        try {
            // Nothing to do if expanded
            if (expandedBlock) return;

            // A reference to this content block container
            const thisNode = document.getElementById(`cbc-${props.name}-main`);
            if (!thisNode) {
                console.error('ContentBlockContainer: Could not find cbc-${props.name}-main in updateDimensions');
                return;
            }

            // The current Width of this content block container
            const currentWidth = thisNode.offsetWidth;
            const currentHeight = thisNode.offsetHeight;

            // Structure is content-block-container > content-block-container__content > content-block
            const ancestorCount = getParents(thisNode, '.content-block-container').length;
            const totalChildren = React.Children.count(children);

            // Try to even out the number of widgets per line
            const maxChildrenPerRow = Math.trunc(currentWidth / (props.breakWidth ?? 550));
            let ideal = Math.max(1, Math.ceil(Math.sqrt(totalChildren)));
            let gridColumns = currentWidth < (props.breakWidth ?? 550)
                              ? 1
                              : Math.min(totalChildren, maxChildrenPerRow, ideal);

            // No stragglers!  Don't leave just one on the last row if there are more than two columns
            if (isMobileView()) gridColumns = 1;
            else if (totalChildren > 3 && gridColumns > 2 && totalChildren % gridColumns === 1) gridColumns--;

            const gridRows = Math.ceil((totalChildren) / gridColumns);
            const containerHeight = currentHeight;
            const containerWidth = singleViewMode ? currentWidth : currentWidth / gridColumns;
            const useSmallDisplay = singleViewMode
                                    ? false
                                    : (containerWidth < MOBILE_WIDTH) || !expandedBlock;

            if (gridColumns !== sizeProps.gridColumns ||
                gridRows !== sizeProps.gridRows ||
                containerWidth !== sizeProps.containerWidth ||
                containerHeight !== sizeProps.containerHeight ||
                useSmallDisplay !== sizeProps.useSmallDisplay ||
                ancestorCount !== sizeProps.ancestorCount
            ) setSizeProps({gridColumns, gridRows, containerWidth, containerHeight, useSmallDisplay, ancestorCount});
        }
        catch (err: any) {
            props.onError?.(err);
        }
    }, [children, expandedBlock, props, singleViewMode, sizeProps.ancestorCount, sizeProps.containerHeight, sizeProps.containerWidth, sizeProps.gridColumns, sizeProps.gridRows, sizeProps.useSmallDisplay]);

    // Handle listeners
    useEffect(() => {
        window.addEventListener("resize", updateDimensions);

        return () => {
            window.removeEventListener("resize", updateDimensions);
        }
    }, [updateDimensions]);

    // Save state
    useEffect(() => {
        return () => {
            cacheSave({expandedBlock, selectedTitle, singleViewMode, sizeProps});
        }
    }, [cacheSave, expandedBlock, selectedTitle, singleViewMode, sizeProps]);

    /**
     * Called when the collapse icon is clicked.  Return to normal for this block.
     */
    const onCollapse: CollapseCallback = useCallback((title: string) => {
        if (props.onBeforeCollapse) props.onBeforeCollapse(title);
        props.onCollapseBlock(props.name, title);
        setExpandedBlock('');
        // Collapsing the URL/query-derived expanded block must stick even
        // though the query param is still in the URL (OWA-503)
        if (urlOrQueryTitle === title) setDismissedUrlTitle(title);
        // If collapsing a URL-derived expanded block, navigate back to base path
        if (props.basePath && urlDerivedTitle === title) {
            navigate(props.basePath);
        }
    }, [props, setExpandedBlock, urlDerivedTitle, urlOrQueryTitle, navigate]);

    /**
     * Called when the 'expand' icon is clicked.  Show only the block specified by the title.
     * @param {string} title
     */
    const onExpand = useCallback((title: string) => {
        if (props.onBeforeExpand) props.onBeforeExpand(title);
        props.onExpandBlock(props.name, title);
        setExpandedBlock(title);
    }, [props, setExpandedBlock]);

    /**
     * Add required params to boxes
     */
    const enrichBoxes = useCallback((boxes: ContentBlockElement | ContentBlockElement[]) => {
        if (!Array.isArray(boxes)) boxes = boxes ? [boxes] : [];

        const {gridColumns, containerWidth, containerHeight, useSmallDisplay} = sizeProps;

        return boxes
            .flat()
            .filter(box => !!box)
            .filter(box => !box.props.isHidden && !box.props.isHiddenOverride)
            .map((box, index) => {
                const isExpanded = singleViewMode ? false : box.props.title === effectiveExpandedBlock;
                const overrideHeight = !isExpanded && !!(props.overrideHeight ?? 0) ? props.overrideHeight : undefined;
                const childProps: ContentBlockChildProps & {
                    parentKey: string,
                    key: string
                } = {
                    parentKey: key,
                    key: `CBC-${props.name}-${box.props.title}`,
                    isExpanded,
                    isHidden: effectiveExpandedBlock ? (box.props.title !== effectiveExpandedBlock) : false,
                    onExpand: singleViewMode ? noop : onExpand,
                    onCollapse: singleViewMode ? noop : onCollapse,
                    gridColumns: singleViewMode ? 1 : gridColumns,
                    gridRows: singleViewMode ? 1 : boxes.length / gridColumns,
                    gridNumber: singleViewMode ? 1 : index + 1,
                    containerWidth: containerWidth,
                    containerHeight: containerHeight,
                    overrideHeight,
                    useSmallDisplay: singleViewMode ? false : useSmallDisplay,
                    singleViewMode: singleViewMode,
                }

                // This clone adds the SizeProps to the ContentBlock.
                // It does not add anything to the wrapped component itself.
                // That is done in ContentBlock.
                // Size props are re-applied AFTER box.props so the measured
                // values from this container always win — otherwise an
                // undefined containerWidth on the ContentBlock JSX would
                // override the measurement and propagate downstream.
                const child = React.cloneElement(box, {
                    ...childProps,
                    ...box.props,
                    containerWidth: containerWidth,
                    containerHeight: containerHeight,
                    useSmallDisplay: singleViewMode ? false : useSmallDisplay,
                });

                const displaySizeClass = useSmallDisplay ? 'content-block--size-small' : '';

                // If this column takes up a whole row, its props.span will be -1
                const styles: CSSProperties = {};
                if (!!box.props.span && gridColumns > 1) styles.gridColumn = `1/${box.props.span}`;

                return <div key={`${box.props.title}-${index}`}
                            id={`${box.props.title}-${index}`}
                            className={`content-block-container__block ${displaySizeClass}`}
                            style={styles}
                >
                    <ErrorBoundaryO uniqueKey={`content-block-container-${props.name}`} shouldUseErrorFrame={true}>
                        {child}
                    </ErrorBoundaryO>
                </div>;
            });
    }, [sizeProps, singleViewMode, effectiveExpandedBlock, props.overrideHeight, props.name, key, onExpand, onCollapse]);

    /**
     * Called when in tabbed mode, and a tab is clicked.
     * @param {string} title - the title for the selectedTab
     */
    const onTabClick = useCallback((title: string) => {
        setSelectedTitle(title);
        setSelectedTab(title);

        if (props.basePath) {
            const urlKey = urlKeyMap.get(title);
            if (urlKey) navigate(`${props.basePath}/${urlKey}`);
        }
    }, [setSelectedTab, props.basePath, urlKeyMap, navigate]);

    const scrollToTop = useCallback((e: any) => {
        e.target.parentElement.scrollIntoView({behavior: "smooth", block: 'start', inline: 'start'});
    }, []);

    const toggleSplitView = useCallback(() => {
        const isSplit = locals.getBoolean(`outlaw.splitView.${props.displayName || props.name}`, false);
        locals.setBoolean(`outlaw.splitView.${props.displayName || props.name}`, !isSplit);

        // If the selected tab is also the split tab, we need to select a different tab in the main section
        if (props.splitLabel && [selectedTitle, selectedTab].includes(props.splitLabel)) {
            // Select the tab before or after it
            const {children} = props;
            const kids = (children as ContentBlockElement[])
                .flat()
                .filter(child => !!child);

            const titleBoxes = kids.map((child) => child.props.title);
            let index = titleBoxes.findIndex(ch => [ch].includes(props.splitLabel));
            if (index > 0) onTabClick(titleBoxes[index - 1]);
            else onTabClick(titleBoxes[index + 1]);
        }

        setSplitView(!isSplit);
    }, [selectedTitle, selectedTab, props, onTabClick, setSplitView]);

    /**
     * Called when the view options "Tabbed" view is selected
     */
    const showTabbedView = useCallback(() => {
        setSingleViewMode(true)
        setStoredViewMode('tabbed');
    }, [setStoredViewMode]);

    /**
     * Called when the view options "All" view is selected
     */
    const showAllView = useCallback(() => {
        setSingleViewMode(false);
        setStoredViewMode('all');
        setSplitView(false);
        setExpandedBlock('');
        // Navigate to base path so URL-derived title doesn't force a single expanded block
        if (props.basePath) navigate(props.basePath);
    }, [setSingleViewMode, setSplitView, setStoredViewMode, props.basePath, navigate]);

    const allowSplit = useMemo(() => {
        const {children} = props;
        const kids = Array.isArray(children) ? children.flat().filter(child => !!child) : [children];
        const titleBoxes = kids.map((child) => {
            const {title, displayTitle} = child.props;
            return {title, displayTitle};
        });

        const {gridColumns, containerWidth} = sizeProps;

        return singleViewMode && // Must be in single view mode (tabs)
            (
                splitView || // If split, must have a width of at least 750 px
                (!splitView && gridColumns > 1 && containerWidth > 750) // If not already split, must be at least two grid columns
            ) &&
            titleBoxes.find(tb => [tb.title, tb.displayTitle].includes(props.splitLabel));
    }, [props, sizeProps, singleViewMode, splitView]);

    const renderViewToggles = useMemo(() => {
        const mode = singleViewMode ? 'single' : 'multi';

        if (expandedBlock) return null;

        // Allow the split if:
        // * We are not yet split and there are 2 or more grid columns
        // * We are split, period
        return <div key='toggles'
                    className={`content-block-container__options content-block-container__options-${mode}`}
        >
            {allowSplit && <IconButton type={Icons.SPLITVIEW}
			                           onClick={toggleSplitView}
			                           size='large'
			                           title='Split view to show documents separately'
			                           className={splitView ? 'selected' : ''}
			/>}

            <IconButton type={Icons.WINDOW}
                        onClick={showTabbedView}
                        size='large'
                        title='Show content as tabs in a single box'
            />
            <IconButton type={Icons.BLOCK_VIEW}
                        onClick={showAllView}
                        size='large'
                        title='Show content as separate boxes'
            />
            <IconButton type={Icons.ARROW_TO_TOP}
                        onClick={scrollToTop}
                        size='large'
                        title='Scroll to top of this section'
            />
        </div>;
    }, [singleViewMode, expandedBlock, allowSplit, toggleSplitView, splitView, showTabbedView, showAllView, scrollToTop]);

    /**
     * Render a tabbed interface that shows one window at a time.
     *
     * Questions:
     * - Clearly should be 100% wide, but how tall? Should there be props for this?
     * - If there are a LOT of tabs, and the screen is wide, should we show the tabs down the left side?
     * - If there are only a couple tabs, should we show them on the top?
     *
     * @param {'left'|'right'|null} side
     */
    const renderTabbedInterface = useCallback((side: 'left' | 'right' | null) => {
        // Show a tab bar with the name of each box
        const kids: ContentBlockElement[] = Array.isArray(children) ? children : [children];
        // Tab selection precedence:
        //  1. urlDerivedTitle — basePath mode, where clicking navigates the URL,
        //     so the URL is the reactive source of truth and always wins.
        //  2. selectedTitle — the user's explicit tab click. This MUST outrank the
        //     deep-link sources below; otherwise a deep link (query param / initialTab)
        //     permanently pins the tab and clicking another tab changes only the
        //     highlight, never the content. selectedTitle is '' until the user clicks,
        //     so on first mount the deep-link sources below still pick the initial tab.
        //  3. queryDerivedTitle / props.initialTab — deep-link defaults (on mount).
        //  4. selectedTab — the last tab persisted to localStorage.
        const effectiveTab = urlDerivedTitle ?? (selectedTitle || queryDerivedTitle || props.initialTab || selectedTab);
        const foundIndex = kids.findIndex(k => k.props?.title === effectiveTab);
        const selectedIndex = foundIndex >= 0 ? foundIndex : 0;

        const titleBoxes = kids
            .map((child) => {
                const {title, displayTitle, customStyles, icon} = child.props;
                return {title, displayTitle, customStyles, icon};
            })
            .filter(({title, displayTitle}) => {
                if (side === 'left') return ![title, displayTitle].includes(props.splitLabel);
                else if (side === 'right') return [title, displayTitle].includes(props.splitLabel);
                else return true;
            });

        const titleBar = <TabBar tabs={titleBoxes}
                                 onClick={onTabClick}
                                 iconsOnly={sizeProps.containerWidth / titleBoxes.length <= 110}
                                 name={'cbc-tab-bar-' + props.name}
                                 selectedIndex={selectedIndex}
        />;

        const overrideHeight = !!(props.overrideHeight ?? 0) ? props.overrideHeight : undefined;
        const style: CSSProperties = overrideHeight ? {height: `calc(${overrideHeight} + 2em)`} : null;

        return <div id={`cbc-${props.name}-main`}
                    key='tabbed-ui'
                    className={`content-block-container content-block-container--single`}
                    style={style}
        >
            {titleBar}

            {enrichBoxes(kids[selectedIndex])}
        </div>;
    }, [children, onTabClick, sizeProps.containerWidth, props.name, props.overrideHeight, props.splitLabel, props.initialTab, enrichBoxes, selectedTab, selectedTitle, urlDerivedTitle, queryDerivedTitle]);

    const renderMultiWindowInterface = () => {
        const {gridColumns, gridRows} = sizeProps;

        const numChildren = children.length;
        const width = gridColumns > 1 ? `calc(${100 / gridColumns}% - ${(gridColumns - 1) / gridColumns * 7}px)` : '100%';
        const style: CSSProperties = !!effectiveExpandedBlock ? {} : {
            display: 'grid',
            gap: '7px',
            gridTemplateRows: `repeat(${gridRows}, max-content)`,
            gridTemplateColumns: `repeat(${gridColumns}, ${width})`,
        };

        let boxes: ReactNode[];

        if (numChildren > 1 && effectiveExpandedBlock) {
            boxes = enrichBoxes(
                children.flat()
                    .filter(child => !!child)
                    .filter(child => child.props.title === effectiveExpandedBlock)
            );
        }
        else {
            boxes = enrichBoxes(children);
        }

        // For Safari
        if (props.useFullHeight) style.minHeight = '100%';

        const overrideHeight = !!(props.overrideHeight ?? 0) ? props.overrideHeight : undefined;
        if (overrideHeight) style.height = `calc(${overrideHeight} + 2em)`;

        return <div id={`cbc-${props.name}-main`}
                    key='multi-ui'
                    className={`content-block-container content-block-container--multi content-block-container--multi-${gridColumns}`}
                    style={style}
        >
            {boxes}
        </div>;
    }

    /**
     * Render the main set of tabs/boxes
     */
    const renderMainContent = () => {
        if (singleViewMode) return renderTabbedInterface(splitView ? 'left' : null);
        else return renderMultiWindowInterface();
    }

    /**
     * Show all the blocks, unless expandedBlock is not null, in which case show that one.
     */
    if (props.splitLabel && splitView) {
        // Get the ContentBlock to split off
        const splitBox = children.flat()
            .filter(child => !!child)
            .filter(child => [child.props.title, child.props.displayTitle].includes(props.splitLabel));

        const box = splitBox[0];
        const {title, displayTitle, customStyles, icon} = box.props;

        const rightBox = <div className='content-block-container content-block-container__right-block'
                              key='right-block'>
            <TabBar tabs={[{title, displayTitle, customStyles, icon}]}
                    onClick={noop}
                    name={'TabBar_single_' + props.name}
            />
            {enrichBoxes(splitBox)}
        </div>;

        // Wrap with an error handler
        return <ContentBlockNestingContext.Provider value={nestingDepth + 1}>
            <ErrorBoundaryO uniqueKey={`content-block-container-${props.name}`} shouldUseErrorFrame={true}>
                {showViewToggles && renderViewToggles}
                <div className='content-block-container__splitter' key='splitter'>
                    {renderMainContent()}
                    {rightBox}
                </div>
            </ErrorBoundaryO>
        </ContentBlockNestingContext.Provider>;
    }
    else {
        return <ContentBlockNestingContext.Provider value={nestingDepth + 1}>
            <ErrorBoundaryO uniqueKey={`content-block-container-${props.name}`} shouldUseErrorFrame={true}>
                {showViewToggles && renderViewToggles}
                {/* Keep the main panel under the same keyed parent as split mode. */}
                {props.splitLabel ? <div key='splitter' style={{display: 'contents'}}>
                    {renderMainContent()}
                </div> : renderMainContent()}
            </ErrorBoundaryO>
        </ContentBlockNestingContext.Provider>;
    }
}

export type ContentBlockContainerProps = Pick<RootState, 'contentBlock'> & {
    /** When set, enables URL-based tab selection using this path prefix (e.g., '/financials') */
    basePath?: string;

    /** When set, selects this tab title on mount (used for deep linking). Does not persist to localStorage. */
    initialTab?: string;

    /** The name of the label we will use to split off */
    splitLabel?: string;

    /** Width at which we will break a line into multiple Content Blocks */
    breakWidth?: number;

    /** Optional display name for this CBC */
    displayName?: string;

    /** Which ContentBlock is currently expanded? */
    expandedBlock?: string;

    /** Name for key usage */
    name: string;

    /** Should a different height be used? */
    overrideHeight?: string;

    /** Callback to call before expanding a ContentBlock */
    onBeforeExpand?: (title: string) => void;

    /** Callback to call before collapsing a ContentBlock */
    onBeforeCollapse?: (title: string) => void;

    /** Should the view toggles be shown? */
    showViewToggles?: boolean;

    /** View a single box at a time, using a tabbed interface */
    singleViewMode?: boolean;

    /** Should we insist on 100% usage of screen height? (For Safari browsers) */
    useFullHeight?: boolean;

    /** Children components to render */
    children: ReactNode;

    /** Content block state from Redux */
    contentBlock: any;

    /** Error handler function */
    onError?: (error: Error) => void;

    /** Expand block action */
    onExpandBlock: (containerName: string, blockName: string) => void;

    /** Collapse block action */
    onCollapseBlock: (containerName: string, blockName: string) => void;
};


const mapStateToProps = (state: RootState) => {
    return {
        contentBlock: state.contentBlock,
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onExpandBlock: (containerName: string, blockName: string) => dispatch(ContentBlockActions.expandBlock(containerName, blockName)),
        onCollapseBlock: (containerName: string, blockName: string) => dispatch(ContentBlockActions.collapseBlock(containerName, blockName))
    };
};

// export default connect(mapStateToProps, mapDispatchToProps)(debounceRender(ContentBlockContainer, 100));
export default connect(mapStateToProps, mapDispatchToProps)(ContentBlockContainer);
